import { AlertActionKeys } from './alert';
import Vue from 'vue';
import { Commit, Module } from 'vuex';
import { api } from '../../plugins/salut-api';
import {
	AttendanceImportMatchType,
	BasicFieldType,
	ScheduleFrequency,
	TableItemsOptions,
	TimeslotRecurrenceType,
} from '../../types';
import {
	EventEditStep,
	Event,
	EventDto,
	EventOption,
	EventSchedule,
	EventFeeType,
	EmptyEventFactory,
	EventPaymentType,
	EventApplication,
	EmptyScheduleFactory,
	EmptyOptionFactory,
	EventScheduleInstance,
	EventAttendance,
	EmptyFeeTypeFactory,
	EventAdmissionType,
	EventScheduleProgressCode,
	EventProgress,
	EventAttendanceImportTableItem,
	EventApplicationDto,
	EventStatusCode,
	EventApplicationCache,
} from '../../types/event';
import {
	EventApplicationPageData,
	EventAttendancePageData,
	EventListType,
	EventPageData,
	EventScheduleInstancePageData,
	EventState,
	EventTicketPageData,
	PeriodConstraints,
	RootState,
} from '../types';
import { AuthGetterKeys } from './auth';
import { i18n } from '../../lib';
import { cloneDeep } from 'lodash';

//localStorage keys
const EVENT_ATTENDANCE_FULL_LIST = 'event_attendance_full_list';
const EVENT_PENDING_CHECKINS = 'event_pending_checkins';

const SHOW_SUBMITTING = 'SHOW_SUBMITTING';
const HIDE_SUBMITTING = 'HIDE_SUBMITTING';
const SET_SUBMIT_FROM = 'SET_SUBMIT_FROM';
const UNSET_SUBMIT_FROM = 'UNSET_SUBMIT_FROM';
const SHOW_LOADING = 'SHOW_LOADING';
const HIDE_LOADING = 'HIDE_LOADING';
const SET_LAST_ERROR = 'SET_LAST_ERROR';
const SET_CURRENT_EVENT = 'SET_CURRENT_EVENT';
const SET_CURRENT_CLONE_EVENT = 'SET_CURRENT_CLONE_EVENT';
const SET_CURRENT_EVENT_VALIDATIONS = 'SET_CURRENT_EVENT_VALIDATIONS';
const SET_CURRENT_EVENT_PAYMENT_TYPE_BY_INDEX =
	'SET_CURRENT_EVENT_PAYMENT_TYPE_BY_INDEX';
const SET_ACTIVITY_THEMES = 'SET_ACTIVITY_THEMES';
const SET_ACTIVITY_TYPES = 'SET_ACTIVITY_TYPES';
const SET_SERVICE_TYPES = 'SET_SERVICE_TYPES';
const SET_TARGET_AUDIENCES = 'SET_TARGET_AUDIENCES';
const SET_CURRENT_STEP = 'SET_CURRENT_STEP';
const SET_CURRENT_EVENT_OPTION_BY_POSITION =
	'SET_CURRENT_EVENT_OPTION_BY_POSITION';
const SET_CURRENT_EVENT_OPTION_SCHEDULE_BY_POSITION =
	'SET_CURRENT_EVENT_OPTION_SCHEDULE_BY_POSITION';
const SET_EVENTS = 'SET_EVENTS';
const SET_LAST_EVENT_LIST = 'SET_LAST_EVENT_LIST';
const REMOVE_CURRENT_EVENT_PAYMENT_TYPE_BY_INDEX =
	'REMOVE_CURRENT_EVENT_PAYMENT_TYPE_BY_INDEX';
const SET_EVENT_APPLICATIONS = 'SET_EVENT_APPLICATIONS';
const UNSET_EVENT_APPLICATIONS = 'UNSET_EVENT_APPLICATIONS';
const SET_CURRENT_APPLICATION = 'SET_CURRENT_APPLICATION';
const CLEAR_CURRENT_EVENT_PAYMENT_OPTIONS =
	'CLEAR_CURRENT_EVENT_PAYMENT_OPTIONS';
const ADD_AGE_TO_CURRENT_EVENT_ADDITIONAL_QUESTIONS =
	'ADD_AGE_TO_CURRENT_EVENT_ADDITIONAL_QUESTIONS';
const REMOVE_AGE_FROM_CURRENT_EVENT_ADDITIONAL_QUESTIONS =
	'REMOVE_AGE_FROM_CURRENT_EVENT_ADDITIONAL_QUESTIONS';
const UPDATE_UNIT_ADDITIONAL_QUESTIONS_TO_CURRENT_EVENT =
	'UPDATE_UNIT_ADDITIONAL_QUESTIONS_TO_CURRENT_EVENT';
const SET_CURRENT_ATTENDANCE_OPTION = 'SET_CURRENT_ATTENDANCE_OPTION';
const SET_ATTENDANCE_SCHEDULE_INSTANCE_PAGE_DATA_BY_OPTION_ID =
	'SET_ATTENDANCE_SCHEDULE_INSTANCE_PAGE_DATA_BY_OPTION_ID';
const SET_CURRENT_SCHEDULE_INSTANCE = 'SET_CURRENT_SCHEDULE_INSTANCE';
const SET_ATTENDANCE_LIST = 'SET_ATTENDANCE_LIST';
const SET_OFFLINE_ATTENDANCE_FULL_LIST = 'SET_OFFLINE_ATTENDANCE_FULL_LIST'; // deprecated
const ADD_OFFLINE_CHECKIN = 'ADD_OFFLINE_CHECKIN'; // deprecated
const CLEAR_OFFLINE_CHECKIN = 'CLEAR_OFFLINE_CHECKIN'; // deprecated
const SET_OFFLINE_CHECKIN = 'SET_OFFLINE_CHECKIN'; // deprecated
const SET_ATTENDANCE_FULL_LIST = 'SET_ATTENDANCE_FULL_LIST';
const SET_EVENT_LISTENER_COUNT = 'SET_EVENT_LISTENER_COUNT';
const SET_EVENT_LISTENER = 'SET_EVENT_LISTENER';
const SET_EVENT_TICKETS = 'SET_EVENT_TICKETS';
const SET_DRAFT_APPLICATION = 'SET_DRAFT_APPLICATION';
const SET_ATTENDANCE_IMPORT_DATA = 'SET_ATTENDANCE_IMPORT_DATA';
const SET_ATTENDANCE_IMPORT_DATA_BY_INDEX =
	'SET_ATTENDANCE_IMPORT_DATA_BY_INDEX';
const SET_APPLICATION_CACHE = 'SET_APPLICATION_CACHE';

export const EventActionKeys = {
	setCurrentEvent: 'setCurrentEvent',
	setCurrentCloneEvent: 'setCurrentCloneEvent',
	unsetCurrentEvent: 'unsetCurrentEvent',
	setCurrentEventValidations: 'setCurrentEventValidations',
	setCurrentEventPaymentType: 'setCurrentEventPaymentType',
	removeCurrentEventPaymentType: 'removeCurrentEventPaymentType',
	setCurrentEventOption: 'setCurrentEventOption',
	setCurrentEventOptions: 'setCurrentEventOptions',
	clearCurrentEventOptions: 'clearCurrentEventOptions',
	setEventSchedule: 'setEventSchedule',
	fetchEvent: 'fetchEvent',
	initializeEvent: 'initializeEvent',
	saveCurrentEvent: 'saveCurrentEvent',
	setCurrentStep: 'setCurrentStep',
	fetchActivityThemes: 'fetchActivityThemes',
	fetchActivityTypes: 'fetchActivityTypes',
	fetchServiceTypes: 'fetchServiceTypes',
	fetchTargetAudiences: 'fetchTargetAudiences',
	fetchByListType: 'fetchByListType',
	fetchTicketsByOption: 'fetchTicketsByOption',
	setLastEventList: 'setLastEventList',
	addOption: 'addOption',
	addRegularDateOption: 'addRegularDateOption',
	addTimeOption: 'addTimeOption',
	addCustomOption: 'addCustomOption',
	deleteOptionByIndex: 'deleteOptionByIndex',
	deleteOptionByPosition: 'deleteOptionByPosition',
	addSchedule: 'addSchedule',
	addFeeType: 'addFeeType',
	setCurrentApplication: 'setCurrentApplication',
	fetchApplication: 'fetchApplication',
	unsetApplications: 'unsetApplications',
	fetchApplications: 'fetchApplications',
	clearPaymentOptionsIfCurrentEventIsFree:
		'clearPaymentOptionsIfCurrentEventIsFree',
	updateAgeQuestionForCurrentEventAgeRequirement:
		'updateAgeQuestionForCurrentEventAgeRequirement',
	updateUnitQuestionsForCurrentEvent: 'updateUnitQuestionsForCurrentEvent',
	setCurrentAttendanceOption: 'setCurrentAttendanceOption',
	fetchScheduleInstancesByOptionId: 'fetchScheduleInstancesByOptionId',
	fetchScheduleInstanceById: 'fetchScheduleInstanceById',
	setCurrentScheduleInstance: 'setCurrentScheduleInstance',
	fetchAttendanceList: 'fetchAttendanceList',
	fetchAttendanceOfflineFullList: 'fetchAttendanceOfflineFullList', //deprecated
	bulkCheckInForOfflineRecords: 'bulkCheckInForOfflineRecords', //deprecated
	offlineCheckIn: 'offlineCheckIn', //deprecated
	fetchAttendanceFullList: 'fetchAttendanceFullList',
	attendanceCheckIn: 'attendanceCheckIn',
	attendanceUndoCheckIn: 'attendanceUndoCheckIn',
	attendanceRemarks: 'attendanceRemarks',
	attendanceAbsent: 'attendanceAbsent',
	updateFromLocalStorage: 'updateFromLocalStorage',
	startLocalStorageListener: 'startLocalStorageListener',
	stopLocalStorageListener: 'stopLocalStorageListener',
	fetchAvailableUsers: 'fetchAvailableUsers',
	setDraftApplication: 'setDraftApplication',
	unsetDraftApplication: 'unsetDraftApplication',
	saveDraftApplication: 'saveDraftApplication',
	setAttendanceImportData: 'setAttendanceImportData',
	setAttendanceImportDataByIndex: 'setAttendanceImportDataByIndex',
	clearAttendanceImportData: 'clearAttendanceImportData',
	fetchApplicationsByUserIds: 'fetchApplicationsByUserIds',
	updateApplicationCache: 'updateApplicationCache',
	setEventSubmitting: 'setEventSubmitting',
	unsetEventSubmitting: 'unsetEventSubmitting',
	unsetEventListPageData: 'unsetEventListPageData',
};

export const EventGetterKeys = {
	loading: 'loading',
	submitting: 'submitting',
	submitFrom: 'submitFrom',
	lastError: 'lastError',
	currentEvent: 'currentEvent',
	currentEventValidations: 'currentEventValidations',
	currentStep: 'currentStep',
	lastEventList: 'lastEventList',
	activityThemes: 'activityThemes',
	activityTypes: 'activityTypes',
	serviceTypes: 'serviceTypes',
	targetAudiences: 'targetAudiences',
	currentEventOptions: 'currentEventOptions',
	getOptionByPosition: 'getOptionByPosition',
	pageData: 'pageData',
	currentApplication: 'currentApplication',
	draftApplication: 'draftApplication',
	applicationsPageData: 'applicationsPageData',
	currentEventIsFree: 'currentEventIsFree',
	currentEventIsTicketType: 'currentEventIsTicketType',
	currentEventHasAgeRequirement: 'currentEventHasAgeRequirement',
	getOptionById: 'getOptionById',
	currentApplicationFeeType: 'currentApplicationFeeType',
	getApplicationFeeTypeById: 'getApplicationFeeTypeById',
	currentApplicationOption: 'currentApplicationOption',
	currentApplicationPaymentType: 'currentApplicationPaymentType',
	getScheduleInstancePageDataByOptionId:
		'getScheduleInstancePageDataByOptionId',
	attendancePageData: 'attendancePageData',
	currentScheduleInstance: 'currentScheduleInstance',
	attendanceOfflineFullList: 'attendanceOfflineFullList', // deprecated
	offlineCheckins: 'offlineCheckins', // deprecated
	attendanceFullList: 'attendanceFullList',
	getEventTicketList: 'getEventTicketList',
	getTicketPageDataByOptionId: 'getTicketPageDataByOptionId',
	attendanceImportData: 'attendanceImportData',
	firstFeeTypeIdByScheduleId: 'firstFeeTypeIdByScheduleId',
	allFeeTypesByScheduleId: 'allFeeTypesByScheduleId',
	applicationCache: 'applicationCache',
};

const initialState: EventState = {
	submitting: false,
	submitFrom: null,
	loading: 0,
	lastError: '',
	currentEvent: null,
	currentEventValidations: {},
	currentStep: EventEditStep.basicInfo,
	lastEventList: EventListType.all,
	activityThemes: [],
	activityTypes: [],
	serviceTypes: [],
	targetAudiences: [],
	currentEventOptions: [],
	pageData: {},
	currentApplication: null,
	currentDraftApplication: undefined,
	applicationsPageData: null,
	scheduleInstancePageData: {},
	attendancePageData: {},
	attendanceFullData: {},
	currentAttendanceOption: null,
	currentScheduleInstance: null,
	offlineCheckins: {}, // deprecated
	attendanceOfflineFullList: {}, // deprecated
	attendanceFullList: { items: [], total: 0 },
	localStorageEventListenerCount: 0,
	ticketPageData: {},
	attendanceImportData: [],
	applicationCache: {},
};

function updateAttendance(
	state: EventState,
	commit: Commit,
	attendance: EventAttendance,
) {
	const orgAttendancePageData = state.attendanceFullList;
	const ind = orgAttendancePageData.items.findIndex(
		att => att.id == attendance.id,
	);

	if (ind >= 0) {
		const attendancePageData: any = {
			...orgAttendancePageData,
			items: [...orgAttendancePageData.items],
		};
		attendancePageData.items[ind] = {
			...attendancePageData.items[ind],
			...attendance,
		};

		commit('SET_ATTENDANCE_FULL_LIST', {
			attendancePageData,
		});
	}
}

const store: Module<EventState, RootState> = {
	namespaced: true,

	state: { ...initialState },

	mutations: {
		[SHOW_SUBMITTING](state) {
			state.submitting = true;
		},

		[HIDE_SUBMITTING](state) {
			state.submitting = false;
		},

		[SET_SUBMIT_FROM](state, fromAction) {
			state.submitFrom = fromAction;
		},

		[UNSET_SUBMIT_FROM](state) {
			state.submitFrom = null;
		},

		[SHOW_LOADING](state) {
			state.loading += 1;
		},

		[HIDE_LOADING](state) {
			state.loading -= 1;
		},

		[SET_LAST_ERROR](state, error) {
			state.lastError = error;
		},

		[SET_CURRENT_EVENT](state, event) {
			state.currentEvent = event;
		},

		[SET_CURRENT_CLONE_EVENT](
			state,
			{
				event,
				ownerCenterId,
				mainNotificationEmail,
			}: {
				event: EventDto | Event;
				ownerCenterId: number;
				mainNotificationEmail: string;
			},
		) {
			const clonedEvent = cloneDeep(event);
			clonedEvent.id = undefined;
			clonedEvent.status = EventStatusCode.draft;
			clonedEvent.createdAt = undefined;
			clonedEvent.updatedAt = undefined;
			clonedEvent.eventLink = undefined;
			clonedEvent.cancelReason = undefined;

			// schedule fields
			clonedEvent.options?.forEach(option => {
				option.id = undefined;
				option.event = undefined;
				option.eventId = undefined;
				option.createdAt = undefined;
				option.updatedAt = undefined;
				option.feeTypes?.forEach(feeType => {
					feeType.id = undefined;
				});

				option.schedules?.forEach(schedule => {
					schedule.id = undefined;
					schedule.option = undefined;
					schedule.optionId = undefined;
					schedule.rundownSessions?.forEach(rundown => {
						rundown.id = undefined;
						rundown.schedule = undefined;
						rundown.scheduleId = undefined;
					});
					schedule.createdAt = undefined;
					schedule.updatedAt = undefined;
				});
			});

			if (clonedEvent.isInternal && clonedEvent.options?.[0]?.schedules?.[0]) {
				// remove imported schedules
				clonedEvent.options[0].schedules = clonedEvent.options[0].schedules.filter(
					schedule => !schedule.isSingleDay,
				);
			}

			clonedEvent.paymentTypes = clonedEvent.paymentTypes?.filter(
				paymentType => !paymentType.isInternal,
			);

			clonedEvent.paymentTypes?.forEach(paymentType => {
				paymentType.id = undefined;
				paymentType.event = undefined;
			});

			// other settings fields
			clonedEvent.collaboratorAllCenters = false;
			clonedEvent.collaboratorCenterIds = [];
			clonedEvent.ownerCenterId = ownerCenterId;
			clonedEvent.mainNotificationEmail = mainNotificationEmail;
			clonedEvent.extraNotificationEmails = [];

			// technically these value should be zero on new create
			// unless the clone event logic fully revamped
			// And this clone logic is further safeguard on BE when update on first create event
			clonedEvent.usedQuota = 0;
			clonedEvent.pendingApplications = 0;

			clonedEvent.addedToReports = undefined;

			state.currentEvent = clonedEvent;
		},

		[SET_CURRENT_EVENT_VALIDATIONS](state, validations) {
			state.currentEventValidations = validations;
		},

		[SET_CURRENT_EVENT_PAYMENT_TYPE_BY_INDEX](
			state,
			{ index, paymentType }: { index: number; paymentType: EventPaymentType },
		) {
			if (!state.currentEvent?.paymentTypes)
				throw new Error('Missing payment types of current event');

			Vue.set(state.currentEvent.paymentTypes, index, paymentType);
		},

		[SET_ACTIVITY_THEMES](state, data) {
			state.activityThemes = data;
		},

		[SET_ACTIVITY_TYPES](state, data) {
			state.activityTypes = data;
		},

		[SET_SERVICE_TYPES](state, data) {
			state.serviceTypes = data;
		},

		[SET_TARGET_AUDIENCES](state, data) {
			state.targetAudiences = data;
		},

		[SET_CURRENT_STEP](state, step) {
			state.currentStep = step;
		},

		[SET_CURRENT_EVENT_OPTION_BY_POSITION](
			state,
			{ position, option }: { position: number; option: EventOption },
		) {
			if (!state.currentEvent?.options) {
				throw new Error('Missing event options');
			}
			const optionIndex =
				state.currentEvent.options.findIndex(
					option => option.position === position,
				) ?? -1;
			if (!state.currentEvent) throw new Error('Missing currentEvent');
			if (!state.currentEvent.options) {
				Vue.set(state.currentEvent, 'options', []);
			}
			if (optionIndex < 0) {
				throw new Error(`Event option at position ${position} not found`);
			}

			Vue.set(state.currentEvent.options, optionIndex, option);
		},

		[SET_CURRENT_EVENT_OPTION_SCHEDULE_BY_POSITION](
			state,
			{
				optionPosition,
				schedulePosition,
				schedule,
			}: {
				optionPosition: number;
				schedulePosition: number;
				schedule: EventSchedule;
			},
		) {
			const optionIndex =
				state.currentEvent?.options?.findIndex(
					option => option.position === optionPosition,
				) ?? -1;
			if (!state.currentEvent) throw new Error('Missing currentEvent');
			if (!state.currentEvent.options) {
				Vue.set(state.currentEvent, 'options', []);
			}
			if (optionIndex < 0) {
				throw new Error(`Event option at position ${optionPosition} not found`);
			}

			if (!state.currentEvent.options) throw new Error('Missing currentEvent');
			const option = state.currentEvent.options[optionIndex];
			const scheduleIndex =
				option.schedules?.findIndex(
					schedule => schedule.position === schedulePosition,
				) ?? -1;
			if (scheduleIndex < 0) {
				throw new Error(
					`Event schedule at position ${schedulePosition} not found`,
				);
			}

			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			Vue.set(option.schedules!, scheduleIndex, schedule);
		},

		[REMOVE_CURRENT_EVENT_PAYMENT_TYPE_BY_INDEX](state, index: number) {
			if (!state.currentEvent?.paymentTypes)
				throw new Error('Missing currentEvent.paymentTypes');
			Vue.delete(state.currentEvent?.paymentTypes, index);
		},

		[SET_EVENTS](
			state,
			{
				listType,
				pageData,
			}: { listType: EventListType; pageData: EventPageData },
		) {
			state.pageData = { ...state.pageData, [listType]: pageData };
		},

		[SET_EVENT_TICKETS](
			state,
			{
				optionId,
				pageData,
			}: { optionId: string; pageData: EventTicketPageData },
		) {
			state.ticketPageData = { ...state.ticketPageData, [optionId]: pageData };
		},

		[SET_LAST_EVENT_LIST](state, eventList: EventListType) {
			state.lastEventList = eventList;
		},

		[SET_CURRENT_APPLICATION](state, application: EventApplication) {
			state.currentApplication = application;
		},

		[SET_EVENT_APPLICATIONS](
			state,
			{ pageData }: { pageData: EventApplicationPageData },
		) {
			state.applicationsPageData = pageData;
		},

		[UNSET_EVENT_APPLICATIONS](state) {
			state.applicationsPageData = null;
		},

		[CLEAR_CURRENT_EVENT_PAYMENT_OPTIONS](state) {
			if (!state.currentEvent?.options) {
				return;
			}
			Vue.set(state.currentEvent, 'paymentTypes', []);
		},

		[ADD_AGE_TO_CURRENT_EVENT_ADDITIONAL_QUESTIONS](state) {
			if (!state.currentEvent) {
				return;
			}
			const additionalQuestions = state.currentEvent.additionalQuestions ?? [];
			if (!additionalQuestions.find(question => question.key === 'age'))
				additionalQuestions.unshift({
					key: 'age',
					desc: { en: 'Age', zhHant: '年齡', zhHans: '年龄' },
					type: BasicFieldType.INTEGER,
					mandatory: true,
					protected: true,
				});
			Vue.set(state.currentEvent, 'additionalQuestions', additionalQuestions);
		},

		[REMOVE_AGE_FROM_CURRENT_EVENT_ADDITIONAL_QUESTIONS](state) {
			if (!state.currentEvent?.additionalQuestions) {
				return;
			}
			Vue.set(
				state.currentEvent,
				'additionalQuestions',
				state.currentEvent.additionalQuestions.filter(question => {
					return question.key !== 'age';
				}),
			);
		},

		[UPDATE_UNIT_ADDITIONAL_QUESTIONS_TO_CURRENT_EVENT](state, { questions }) {
			if (!state.currentEvent) {
				return;
			}
			const additionalQuestions =
				state.currentEvent.additionalQuestions?.filter(
					question => !question.key?.startsWith('unit_'),
				) ?? [];
			questions.forEach((question: any) => {
				additionalQuestions.push({
					...question,
					protected: true,
				});
			});
			Vue.set(state.currentEvent, 'additionalQuestions', additionalQuestions);
		},

		[SET_CURRENT_ATTENDANCE_OPTION](state, option: EventOption) {
			state.currentAttendanceOption = option;
		},

		[SET_ATTENDANCE_SCHEDULE_INSTANCE_PAGE_DATA_BY_OPTION_ID](
			state,
			{
				optionId,
				scheduleInstancePageData,
			}: {
				optionId: string;
				scheduleInstancePageData: EventScheduleInstancePageData;
			},
		) {
			Vue.set(
				state.scheduleInstancePageData,
				optionId,
				scheduleInstancePageData,
			);
		},

		[SET_CURRENT_SCHEDULE_INSTANCE](
			state,
			scheduleInstance: EventScheduleInstance,
		) {
			state.currentScheduleInstance = scheduleInstance;
		},

		[SET_ATTENDANCE_LIST](
			state,
			{
				scheduleInstanceId,
				attendancePageData,
			}: { scheduleInstanceId: string; attendancePageData: EventAttendance[] },
		) {
			Vue.set(state.attendancePageData, scheduleInstanceId, attendancePageData);
		},

		[SET_ATTENDANCE_FULL_LIST](
			state,
			{
				attendancePageData,
			}: {
				attendancePageData: EventAttendancePageData;
			},
		) {
			Vue.set(state, 'attendanceFullList', attendancePageData);
		},

		[SET_OFFLINE_ATTENDANCE_FULL_LIST](
			state,
			{
				userId,
				scheduleInstanceId,
				attendancePageData,
			}: {
				userId: string;
				scheduleInstanceId: string;
				attendancePageData: EventAttendance[];
			},
		) {
			const key = `${EVENT_ATTENDANCE_FULL_LIST}_${userId}`;
			const localData = JSON.parse(localStorage.getItem(key) || '{}');
			localData[scheduleInstanceId] = attendancePageData;
			localStorage.setItem(key, JSON.stringify(localData));
			Vue.set(state, 'attendanceOfflineFullList', localData);
		},

		[SET_OFFLINE_CHECKIN](state, { checkins }) {
			state.offlineCheckins = checkins;
		},

		[ADD_OFFLINE_CHECKIN](
			state,
			{
				userId,
				scheduleInstanceId,
				attendanceId,
				time,
			}: {
				userId: string;
				attendanceId: string;
				scheduleInstanceId: string;
				time: Date;
			},
		) {
			const key = `${EVENT_PENDING_CHECKINS}_${userId}`;

			// schedule ID, local time
			// [{id: '7aa2aa1e-65d7-42a1-a00a-29a52d6fcab8', time: '2021-06-06T02:03.023Z'}]

			const items = JSON.parse(localStorage.getItem(key) || '{}');
			items[scheduleInstanceId] = items[scheduleInstanceId] ?? [];
			if (
				!items[scheduleInstanceId].find(
					(checkin: any) => checkin.id === attendanceId,
				)
			) {
				items[scheduleInstanceId].push({ id: attendanceId, time });
				localStorage.setItem(key, JSON.stringify(items));
				Vue.set(
					state.offlineCheckins,
					scheduleInstanceId,
					items[scheduleInstanceId],
				);
			}
		},

		[CLEAR_OFFLINE_CHECKIN](
			state,
			{
				userId,
				scheduleInstanceId,
			}: { userId: string; scheduleInstanceId: string },
		) {
			const key = `${EVENT_PENDING_CHECKINS}_${userId}`;
			const items = JSON.parse(localStorage.getItem(key) || '{}');
			delete items[scheduleInstanceId];
			localStorage.setItem(key, JSON.stringify(items));
		},

		[SET_EVENT_LISTENER](state, { listener }) {
			if (!state.localStorageEventListenerFunction) {
				state.localStorageEventListenerFunction = listener;
				//note: this event will only be triggered with update in other windows
				window.addEventListener('storage', listener);
			}
		},

		[SET_EVENT_LISTENER_COUNT](state, { count }) {
			state.localStorageEventListenerCount = count;
		},

		[SET_DRAFT_APPLICATION](state, draftApplication: EventApplicationDto) {
			state.currentDraftApplication = draftApplication;
		},

		[SET_ATTENDANCE_IMPORT_DATA](
			state,
			data: EventAttendanceImportTableItem[],
		) {
			state.attendanceImportData = data;
		},

		[SET_ATTENDANCE_IMPORT_DATA_BY_INDEX](
			state,
			{ item, index }: { item: EventAttendanceImportTableItem; index: number },
		) {
			Vue.set(state.attendanceImportData, index, item);
		},

		[SET_APPLICATION_CACHE](state, applicationCache: EventApplicationCache) {
			state.applicationCache = applicationCache;
		},
	},

	actions: {
		[EventActionKeys.setCurrentEvent]({ commit }, { event }) {
			commit(SET_CURRENT_EVENT, event);
		},

		async [EventActionKeys.setCurrentCloneEvent](
			{ commit },
			{
				event,
				ownerCenterId,
				mainNotificationEmail,
			}: {
				event: Event;
				ownerCenterId: number;
				mainNotificationEmail: string;
			},
		) {
			commit(SET_CURRENT_EVENT_VALIDATIONS, {});

			// To also get rundown sessions of schedules which are not included when fetching events
			const eventToBeCloned = await api.event.fetchEventById(event.id);
			commit(SET_CURRENT_CLONE_EVENT, {
				event: eventToBeCloned,
				ownerCenterId,
				mainNotificationEmail,
			});
		},

		[EventActionKeys.unsetCurrentEvent]({ commit }) {
			commit(SET_CURRENT_EVENT_VALIDATIONS, {});
			commit(SET_CURRENT_EVENT, null);
		},

		[EventActionKeys.setCurrentEventValidations]({ commit }, { validations }) {
			commit(SET_CURRENT_EVENT_VALIDATIONS, validations);
		},

		async [EventActionKeys.fetchEvent]({ commit }, { id }) {
			const event = await api.event.fetchEventById(id);
			commit(SET_CURRENT_EVENT_VALIDATIONS, event.validations);
			delete event.validations;
			commit(SET_CURRENT_EVENT, event);
		},

		[EventActionKeys.initializeEvent](
			{ commit },
			{
				centerId,
				userEmail,
				partialEvent,
			}: { centerId: number; userEmail: string; partialEvent: Partial<Event> },
		) {
			commit(SET_CURRENT_EVENT_VALIDATIONS, {});
			commit(
				SET_CURRENT_EVENT,
				EmptyEventFactory(centerId, userEmail, partialEvent),
			);
		},

		async [EventActionKeys.saveCurrentEvent]({ state, commit }) {
			if (!state.currentEvent) throw new Error('Empty currentEvent');
			let event: Event;
			// isNew field only used in frontend to track if the question is new
			state.currentEvent.additionalQuestions = state.currentEvent.additionalQuestions?.map(
				question => {
					delete question.isNew;
					return question;
				},
			);
			if (state.currentEvent.id) {
				event = await api.event.updateEvent(
					state.currentEvent,
					state.currentEventValidations,
				);
			} else {
				event = await api.event.createEvent(
					state.currentEvent,
					state.currentEventValidations,
				);
			}
			if (!event) throw new Error('Empty event save result');
			commit(SET_CURRENT_EVENT, event);
			return event;
		},

		[EventActionKeys.setCurrentStep]({ commit }, { step }) {
			commit(SET_CURRENT_STEP, step);
		},

		async [EventActionKeys.fetchActivityThemes]({ commit }) {
			const activityThemes = await api.event.fetchActivityThemes();
			commit(SET_ACTIVITY_THEMES, activityThemes);
		},

		async [EventActionKeys.fetchActivityTypes]({ commit }) {
			const activityTypes = await api.event.fetchActivityTypes();
			commit(SET_ACTIVITY_TYPES, activityTypes);
		},

		async [EventActionKeys.fetchServiceTypes]({ commit }) {
			const serviceTypes = await api.event.fetchServiceTypes();
			commit(SET_SERVICE_TYPES, serviceTypes);
		},

		async [EventActionKeys.fetchTargetAudiences]({ commit }) {
			const activityThemes = await api.event.fetchTargetAudiences();
			commit(SET_TARGET_AUDIENCES, activityThemes);
		},

		[EventActionKeys.setCurrentEventOption](
			{ commit, dispatch },
			{ position, option }: { position: number; option: EventOption },
		) {
			commit(SET_CURRENT_EVENT_OPTION_BY_POSITION, { position, option });
			dispatch(EventActionKeys.clearPaymentOptionsIfCurrentEventIsFree);
		},

		[EventActionKeys.setCurrentEventOptions](
			{ getters, commit, dispatch },
			{ options }: { options: EventOption[] },
		) {
			const currentEvent: Event | EventDto =
				getters[EventGetterKeys.currentEvent];
			commit(SET_CURRENT_EVENT, {
				...currentEvent,
				options: options.map((option, index) => ({
					...option,
					position: index, // re-assign position index
				})),
			});
			dispatch(EventActionKeys.clearPaymentOptionsIfCurrentEventIsFree);
		},

		[EventActionKeys.clearCurrentEventOptions]({ dispatch }) {
			dispatch(EventActionKeys.setCurrentEventOptions, { options: [] });
		},

		[EventActionKeys.addOption](
			{ getters, commit },
			{ option }: { option: EventOption },
		) {
			const currentEvent: Event | EventDto =
				getters[EventGetterKeys.currentEvent];

			const currentEventOptions: EventOption[] =
				getters[EventGetterKeys.currentEventOptions];

			if (currentEvent.admissionForAllOptions && currentEventOptions.length) {
				const template = currentEventOptions[0];
				option.quotaType = template.quotaType;
				option.totalQuota = template.totalQuota;
				option.feeTypes = template.feeTypes?.map(ft => ({
					...ft,
					id: undefined,
				}));
			}

			option = { ...option, position: currentEventOptions.length };

			const options: EventOption[] = [...currentEventOptions, option];
			commit(SET_CURRENT_EVENT, {
				...currentEvent,
				options,
			});
		},

		[EventActionKeys.addRegularDateOption]({ dispatch }) {
			let option: EventOption = EmptyOptionFactory();
			option = {
				...option,
				// Date
				frequency: ScheduleFrequency.regular,
				isSingleDay: false,
				recurrenceType: TimeslotRecurrenceType.daily,
				startDate: null,
				endDate: null,
			};
			dispatch(EventActionKeys.addOption, { option });
		},

		[EventActionKeys.addTimeOption]({ dispatch }) {
			const option: EventOption = EmptyOptionFactory();
			dispatch(EventActionKeys.addOption, { option });
		},

		[EventActionKeys.addCustomOption]({ dispatch }) {
			const option: EventOption = EmptyOptionFactory();
			dispatch(EventActionKeys.addOption, { option });
		},

		[EventActionKeys.deleteOptionByIndex](
			{ getters, dispatch },
			{ optionIndex }: { optionIndex: number },
		) {
			let options: EventOption[] = getters[EventGetterKeys.currentEventOptions];
			if (
				options[optionIndex].schedules?.find(schedule =>
					[
						EventScheduleProgressCode.ongoing,
						EventScheduleProgressCode.completed,
					].includes(schedule.progress ?? EventScheduleProgressCode.scheduled),
				)
			) {
				dispatch(
					`alert/${AlertActionKeys.error}`,
					i18n.t(
						'pages.events.options.errors.cannotDeleteOptionWithStartedSchedule',
					),
					{
						root: true,
					},
				);
				return;
			}

			options = options.filter((_, index: number) => index !== optionIndex);
			dispatch(EventActionKeys.setCurrentEventOptions, { options });
		},

		[EventActionKeys.deleteOptionByPosition](
			{ getters, dispatch },
			{ optionPosition }: { optionPosition: number },
		) {
			let options: EventOption[] = getters[EventGetterKeys.currentEventOptions];
			if (
				options
					.find(option => option.position === optionPosition)
					?.schedules?.some(schedule =>
						[
							EventScheduleProgressCode.ongoing,
							EventScheduleProgressCode.completed,
						].includes(
							schedule.progress ?? EventScheduleProgressCode.scheduled,
						),
					)
			) {
				dispatch(
					`alert/${AlertActionKeys.error}`,
					i18n.t(
						'pages.events.options.errors.cannotDeleteOptionWithStartedSchedule',
					),
					{
						root: true,
					},
				);
				return;
			}

			options = options.filter(option => option.position !== optionPosition);
			dispatch(EventActionKeys.setCurrentEventOptions, { options });
		},

		[EventActionKeys.setEventSchedule](
			{ commit },
			{
				optionPosition,
				schedulePosition,
				schedule,
			}: {
				optionPosition: number;
				schedulePosition: number;
				schedule: EventSchedule;
			},
		) {
			commit(SET_CURRENT_EVENT_OPTION_SCHEDULE_BY_POSITION, {
				optionPosition,
				schedulePosition,
				schedule,
			});
		},

		[EventActionKeys.addSchedule](
			{ getters, commit, dispatch },
			{
				optionPosition,
				initData,
			}: { optionPosition: number; initData?: Partial<EventSchedule> },
		) {
			const option: EventOption | undefined = getters[
				EventGetterKeys.getOptionByPosition
			](optionPosition);
			if (!option) throw new Error('No option');
			const oldSchedules = option?.schedules || [];
			const maxPosition = oldSchedules.reduce(
				(max, schedule) => Math.max(schedule.position, max),
				-1,
			);
			const newSchedule: EventSchedule = {
				...EmptyScheduleFactory(),
				startTime: option.startTime,
				endTime: option.endTime,
				position: maxPosition + 1,
				isSingleDay: option.frequency === ScheduleFrequency.oneOff,
				...initData,
			};

			commit(SET_CURRENT_EVENT_OPTION_BY_POSITION, {
				position: optionPosition,
				option: {
					...option,
					schedules: [...oldSchedules, newSchedule],
				},
			});
			dispatch(EventActionKeys.clearPaymentOptionsIfCurrentEventIsFree);
		},

		[EventActionKeys.addFeeType](
			{ getters, commit, dispatch },
			{
				optionPosition,
				isByFeeType,
			}: {
				optionPosition: number;
				isByFeeType: boolean;
			},
		) {
			const option: EventOption | undefined = getters[
				EventGetterKeys.getOptionByPosition
			](optionPosition);
			if (!option) throw new Error('No option');
			const currentEvent: Event | EventDto =
				getters[EventGetterKeys.currentEvent];

			const oldFeeTypes = option?.feeTypes || [];
			const isTicketing =
				currentEvent.admissionType === EventAdmissionType.ticket;
			const newFeeType: EventFeeType = EmptyFeeTypeFactory({
				isTicketing,
				isByFeeType,
			});

			commit(SET_CURRENT_EVENT_OPTION_BY_POSITION, {
				position: optionPosition,
				option: {
					...option,
					feeTypes: [...oldFeeTypes, newFeeType],
				},
			});
			dispatch(EventActionKeys.clearPaymentOptionsIfCurrentEventIsFree);
		},

		[EventActionKeys.setCurrentEventPaymentType](
			{ commit },
			{ index, paymentType }: { index: number; paymentType: EventPaymentType },
		) {
			commit(SET_CURRENT_EVENT_PAYMENT_TYPE_BY_INDEX, { index, paymentType });
		},

		[EventActionKeys.removeCurrentEventPaymentType](
			{ commit },
			{ index }: { index: number },
		) {
			commit(REMOVE_CURRENT_EVENT_PAYMENT_TYPE_BY_INDEX, index);
		},

		async [EventActionKeys.fetchByListType](
			{ commit },
			{
				options,
				listType,
				progress,
				admissionType,
				reportId,
				periodConstraints,
				centerIds,
			}: {
				options: TableItemsOptions;
				listType: EventListType;
				progress?: EventProgress | EventProgress[];
				admissionType?: EventAdmissionType | EventAdmissionType[];
				reportId?: string;
				periodConstraints?: PeriodConstraints;
				centerIds?: number[];
			},
		) {
			try {
				commit(SHOW_LOADING);
				const data = await api.event.fetchEvents({
					options,
					listType,
					progress,
					admissionType,
					reportId,
					periodConstraints,
					centerIds,
				});
				commit(SET_EVENTS, {
					listType,
					pageData: { items: data?.result, total: data?.total },
				});
				// } catch (error) {
				// commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		[EventActionKeys.unsetEventListPageData](
			{ commit },
			listType: EventListType,
		) {
			commit(SET_EVENTS, { listType, pageData: { items: [], total: 0 } });
		},

		async [EventActionKeys.fetchTicketsByOption](
			{ commit },
			{
				options,
				optionId,
			}: {
				options: TableItemsOptions;
				optionId: string;
			},
		) {
			try {
				commit(SHOW_LOADING);
				const data = await api.event.fetchEventTicketsByOptionId(
					options,
					optionId,
				);
				commit(SET_EVENT_TICKETS, {
					optionId,
					pageData: { items: data?.result, total: data?.total },
				});
				// } catch (error) {
				// commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		[EventActionKeys.setLastEventList](
			{ commit },
			{ eventList }: { eventList: string },
		) {
			commit(SET_LAST_EVENT_LIST, eventList);
		},

		/**
		 * Applications
		 */
		[EventActionKeys.setCurrentApplication]({ commit }, { application }) {
			commit(SET_CURRENT_APPLICATION, application);
		},

		async [EventActionKeys.fetchApplication]({ commit }, { id }) {
			const application = await api.event.fetchEventApplicationById(id);
			commit(SET_CURRENT_APPLICATION, application);
		},

		async [EventActionKeys.fetchApplications](
			{ commit },
			{
				options,
				eventId,
			}: {
				options: TableItemsOptions;
				eventId: string;
			},
		) {
			try {
				commit(SHOW_LOADING);
				const data = await api.event.fetchEventApplications(options, eventId);
				commit(SET_EVENT_APPLICATIONS, {
					eventId,
					pageData: { items: data?.result, total: data?.total },
				});
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [EventActionKeys.unsetApplications]({ commit }) {
			commit(UNSET_EVENT_APPLICATIONS);
		},

		[EventActionKeys.clearPaymentOptionsIfCurrentEventIsFree]({
			commit,
			getters,
		}) {
			if (getters[EventGetterKeys.currentEventIsFree]) {
				commit(CLEAR_CURRENT_EVENT_PAYMENT_OPTIONS);
			}
		},

		[EventActionKeys.updateAgeQuestionForCurrentEventAgeRequirement]({
			commit,
			getters,
		}) {
			if (getters[EventGetterKeys.currentEventHasAgeRequirement]) {
				commit(ADD_AGE_TO_CURRENT_EVENT_ADDITIONAL_QUESTIONS);
			} else {
				commit(REMOVE_AGE_FROM_CURRENT_EVENT_ADDITIONAL_QUESTIONS);
			}
		},

		async [EventActionKeys.updateUnitQuestionsForCurrentEvent]({
			state,
			commit,
		}) {
			if (state.currentEvent && state.currentEvent.ownerCenterId) {
				const unitQuestions = await api.job.fetchPresetQuestions({
					centerId: state.currentEvent.ownerCenterId,
					type: 'event',
				});
				commit(UPDATE_UNIT_ADDITIONAL_QUESTIONS_TO_CURRENT_EVENT, {
					questions: unitQuestions.flatMap(uq => uq.additionalQuestions),
				});
			}
		},

		/**
		 * attendance
		 */
		[EventActionKeys.setCurrentAttendanceOption](
			{ commit },
			{ option }: { option: EventOption },
		) {
			commit(SET_CURRENT_ATTENDANCE_OPTION, option);
		},

		async [EventActionKeys.fetchScheduleInstancesByOptionId](
			{ commit },
			{
				options,
				eventId,
				optionId,
			}: {
				options: TableItemsOptions;
				eventId: string;
				optionId: string;
			},
		) {
			try {
				commit(SHOW_LOADING);
				const { result, total } = await api.event.fetchEventScheduleInstances(
					options,
					eventId,
					optionId,
				);
				commit(SET_ATTENDANCE_SCHEDULE_INSTANCE_PAGE_DATA_BY_OPTION_ID, {
					optionId,
					scheduleInstancePageData: { items: result, total: total },
				});
			} catch (error) {
				commit(SET_LAST_ERROR, error);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [EventActionKeys.fetchScheduleInstanceById](
			{ commit },
			{ id }: { id: string },
		) {
			const scheduleInstance = await api.event.fetchEventScheduleInstance(id);
			commit(SET_CURRENT_SCHEDULE_INSTANCE, scheduleInstance);
		},

		async [EventActionKeys.setCurrentScheduleInstance](
			{ commit },
			{ scheduleInstance }: { scheduleInstance: EventScheduleInstance },
		) {
			commit(SET_CURRENT_SCHEDULE_INSTANCE, scheduleInstance);
		},

		async [EventActionKeys.fetchAttendanceList](
			{ commit },
			{
				options,
				scheduleInstanceId,
			}: {
				options: TableItemsOptions;
				scheduleInstanceId: string;
			},
		) {
			const data = await api.event.fetchAttendanceList(
				options,
				scheduleInstanceId,
			);
			commit(SET_ATTENDANCE_LIST, {
				scheduleInstanceId,
				attendancePageData: { items: data?.result, total: data?.total },
			});
		},

		async [EventActionKeys.fetchAttendanceFullList](
			{ commit },
			{
				scheduleInstanceId,
			}: {
				scheduleInstanceId: string;
			},
		) {
			try {
				commit(SHOW_LOADING);
				const options = {
					page: 1,
					itemsPerPage: 5000,
					sortBy: [],
					sortDesc: [],
					groupBy: [],
					groupDesc: [],
					multiSort: false,
					mustSort: false,
					search: { headers: [], text: '' },
					filter: [],
					filterParams: {},
				};

				const data = await api.event.fetchAttendanceList(
					options,
					scheduleInstanceId,
				);

				await commit(SET_ATTENDANCE_FULL_LIST, {
					attendancePageData: { items: data?.result, total: data?.total },
				});
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [EventActionKeys.fetchAttendanceOfflineFullList](
			{ commit, rootGetters },
			{
				scheduleInstanceId,
			}: {
				scheduleInstanceId: string;
			},
		) {
			try {
				commit(SHOW_LOADING);
				const options = {
					page: 1,
					itemsPerPage: 5000,
					sortBy: [],
					sortDesc: [],
					groupBy: [],
					groupDesc: [],
					multiSort: false,
					mustSort: false,
					search: { headers: [], text: '' },
					filter: [],
					filterParams: {},
				};

				const data = await api.event.fetchAttendanceList(
					options,
					scheduleInstanceId,
				);

				const userId = rootGetters[`auth/${AuthGetterKeys.userId}`];
				await commit(SET_OFFLINE_ATTENDANCE_FULL_LIST, {
					userId,
					scheduleInstanceId,
					attendancePageData: { items: data?.result, total: data?.total },
				});
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [EventActionKeys.bulkCheckInForOfflineRecords](
			{ commit, rootGetters, dispatch },
			{ scheduleInstanceId },
		) {
			const userId = rootGetters[`auth/${AuthGetterKeys.userId}`];
			const key = `${EVENT_PENDING_CHECKINS}_${userId}`;
			const localCheckins = JSON.parse(localStorage.getItem(key) ?? '{}');
			const checkIns = localCheckins[scheduleInstanceId];

			if (checkIns?.length) {
				await api.event.saveRecordedCheckin(checkIns);
			}
			await dispatch(EventActionKeys.fetchAttendanceOfflineFullList, {
				scheduleInstanceId,
			});
			commit(CLEAR_OFFLINE_CHECKIN, { userId, scheduleInstanceId });
		},

		async [EventActionKeys.offlineCheckIn](
			{ commit, rootGetters },
			{ scheduleInstanceId, attendanceId, time },
		) {
			const userId = rootGetters[`auth/${AuthGetterKeys.userId}`];
			commit(ADD_OFFLINE_CHECKIN, {
				userId,
				scheduleInstanceId,
				attendanceId,
				time,
			});
		},

		async [EventActionKeys.attendanceCheckIn](
			{ commit, state },
			{ attendanceId },
		) {
			const attendance = await api.event.checkin(attendanceId);
			updateAttendance(state, commit, attendance);
		},

		async [EventActionKeys.attendanceUndoCheckIn](
			{ commit, state },
			{ attendanceId },
		) {
			const attendance = await api.event.unconfirm(attendanceId);
			updateAttendance(state, commit, attendance);
		},

		async [EventActionKeys.attendanceAbsent](
			{ commit, state },
			{ attendanceId, absentReason },
		) {
			const attendance = await api.event.attendanceAbsent(
				attendanceId,
				absentReason,
			);
			updateAttendance(state, commit, attendance);
		},

		async [EventActionKeys.attendanceRemarks](
			{ commit, state },
			{ admissionType, attendanceId, remarks },
		) {
			const attendance = await api.event.updateAttendanceRemarks(
				admissionType,
				attendanceId,
				remarks,
			);
			if (admissionType === EventAdmissionType.checkIn) {
				updateAttendance(state, commit, attendance);
			}
		},

		async [EventActionKeys.updateFromLocalStorage]({
			commit,
			state,
			rootGetters,
		}) {
			try {
				commit(SHOW_LOADING);
				const userId = rootGetters[`auth/${AuthGetterKeys.userId}`];
				const key1 = `${EVENT_PENDING_CHECKINS}_${userId}`;
				const checkins = JSON.parse(localStorage.getItem(key1) || '{}');
				commit(SET_OFFLINE_CHECKIN, {
					checkins,
				});

				const key2 = `${EVENT_ATTENDANCE_FULL_LIST}_${userId}`;
				const localData = JSON.parse(localStorage.getItem(key2) || '{}');

				for (const scheduleInstanceId of Object.keys(
					state.attendanceOfflineFullList,
				)) {
					commit(SET_OFFLINE_ATTENDANCE_FULL_LIST, {
						userId,
						scheduleInstanceId,
						attendancePageData: localData[scheduleInstanceId],
					});
				}
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [EventActionKeys.startLocalStorageListener]({
			commit,
			dispatch,
			state,
		}) {
			let count = state.localStorageEventListenerCount;
			if (count < 0) {
				count = 0;
			}

			if (count === 0) {
				commit(SET_EVENT_LISTENER, {
					listener: () => {
						dispatch(EventActionKeys.updateFromLocalStorage);
					},
				});
			}

			count = count + 1; //don't use ++, count is reference to state field
			commit(SET_EVENT_LISTENER_COUNT, {
				count,
			});
		},

		async [EventActionKeys.stopLocalStorageListener]({ commit, state }) {
			let count = state.localStorageEventListenerCount;
			count = count - 1; //don't use --, count is reference to state field
			if (count < 0) {
				count = 0;
			}

			if (count === 0 && state.localStorageEventListenerFunction) {
				window.removeEventListener(
					'storage',
					state.localStorageEventListenerFunction,
				);
			}

			commit(SET_EVENT_LISTENER_COUNT, {
				count,
			});
		},

		// application
		[EventActionKeys.setDraftApplication](
			{ commit },
			{ application }: { application: EventApplicationDto },
		) {
			commit(SET_DRAFT_APPLICATION, application);
		},

		[EventActionKeys.unsetDraftApplication]({ commit }) {
			commit(SET_DRAFT_APPLICATION, undefined);
		},

		[EventActionKeys.saveDraftApplication]({
			state,
		}): Promise<EventApplication> {
			if (!state.currentDraftApplication) {
				throw ' Missing draftApplication';
			}
			if (state.currentDraftApplication.id) {
				return api.event.updateEventApplication(
					state.currentDraftApplication.id,
					{
						firstName: state.currentDraftApplication.firstName,
						lastName: state.currentDraftApplication.lastName,
						chineseName: state.currentDraftApplication.chineseName,
						email: state.currentDraftApplication?.email,
						mobileNumber: state.currentDraftApplication.mobileNumber,
					},
				);
			} else {
				return api.event.createEventApplication(state.currentDraftApplication);
			}
		},

		[EventActionKeys.setAttendanceImportData](
			{ commit },
			{ data }: { data: EventAttendanceImportTableItem[] },
		) {
			try {
				commit(SHOW_LOADING);
				commit(SET_ATTENDANCE_IMPORT_DATA, data);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		[EventActionKeys.setAttendanceImportDataByIndex](
			{ commit },
			{ item, index }: { item: EventAttendanceImportTableItem; index: number },
		) {
			try {
				commit(SHOW_LOADING);
				commit(SET_ATTENDANCE_IMPORT_DATA_BY_INDEX, { item, index });
			} finally {
				commit(HIDE_LOADING);
			}
		},

		[EventActionKeys.clearAttendanceImportData]({ commit }) {
			try {
				commit(SHOW_LOADING);
				commit(SET_ATTENDANCE_IMPORT_DATA, []);
			} finally {
				commit(HIDE_LOADING);
			}
		},

		async [EventActionKeys.fetchApplicationsByUserIds](
			{ commit, state },
			{ eventId, userIds }: { eventId: string; userIds: string[] },
		) {
			const applications = await api.event.fetchEventApplicationByEventIdAndUserIds(
				{
					eventId,
					userIds,
				},
			);
			const applicationCache = state.applicationCache;
			for (const application of applications) {
				const userId = application.userId;
				if (!applicationCache[userId]) {
					applicationCache[userId] = {};
				}
				applicationCache[userId][application.eventId] = application;
			}
			commit(SET_APPLICATION_CACHE, applicationCache);
		},

		[EventActionKeys.updateApplicationCache](
			{ commit, state },
			{
				userId,
				application,
			}: { userId: string; application: EventApplication },
		) {
			const applicationCache = state.applicationCache;
			if (!applicationCache[userId]) {
				applicationCache[userId] = {};
			}
			applicationCache[userId][application.eventId] = application;
			commit(SET_APPLICATION_CACHE, applicationCache);
		},

		[EventActionKeys.setEventSubmitting]({ commit }, fromAction) {
			commit(SHOW_SUBMITTING);
			commit(SET_SUBMIT_FROM, fromAction);
		},

		[EventActionKeys.unsetEventSubmitting]({ commit }) {
			commit(HIDE_SUBMITTING);
			commit(UNSET_SUBMIT_FROM);
		},
	},

	getters: {
		[EventGetterKeys.loading]: state => Boolean(state.loading),
		[EventGetterKeys.submitting]: state => state.submitting,
		[EventGetterKeys.submitFrom]: state => state.submitFrom,
		[EventGetterKeys.lastError]: state => state.lastError,
		[EventGetterKeys.currentEvent]: (state): Event | EventDto | null =>
			state.currentEvent
				? {
						...state.currentEvent,
						// sort options
						options: state.currentEvent?.options
							? state.currentEvent.options
									.sort((s1, s2) => s1.position - s2.position)
									.map(option => ({
										...option,
										// sort schedules
										schedules: option.schedules?.sort(
											(s1, s2) => s1.position - s2.position,
										),
										frequency: option.frequency || ScheduleFrequency.oneOff,
									}))
							: undefined,
				  }
				: null,
		[EventGetterKeys.currentEventValidations]: state =>
			state.currentEventValidations,
		[EventGetterKeys.currentStep]: state => state.currentStep,
		[EventGetterKeys.lastEventList]: state => state.lastEventList,
		[EventGetterKeys.activityThemes]: state => state.activityThemes,
		[EventGetterKeys.activityTypes]: state => state.activityTypes,
		[EventGetterKeys.serviceTypes]: state => state.serviceTypes,
		[EventGetterKeys.targetAudiences]: state => state.targetAudiences,
		[EventGetterKeys.currentEventOptions]: (_, getters) =>
			getters[EventGetterKeys.currentEvent]?.options || [],
		[EventGetterKeys.getOptionByPosition]: (_, getters) => (
			optionPosition: number,
		) =>
			(getters[EventGetterKeys.currentEventOptions] as EventOption[]).find(
				option => option.position === optionPosition,
			),
		[EventGetterKeys.pageData]: state => (
			eventListType: EventListType,
		): EventPageData =>
			state.pageData[eventListType] || { items: [], total: 0 },

		[EventGetterKeys.currentApplication]: state => state.currentApplication,
		[EventGetterKeys.draftApplication]: state => state.currentDraftApplication,
		[EventGetterKeys.applicationsPageData]: state => state.applicationsPageData,
		[EventGetterKeys.currentEventIsFree]: (state): boolean =>
			!state.currentEvent?.options ||
			!state.currentEvent.options
				.flatMap(option => option.feeTypes?.map(feeType => feeType.fee) ?? [])
				.some(fee => fee ?? 0 > 0),
		[EventGetterKeys.currentEventIsTicketType]: (state): boolean =>
			state.currentEvent?.admissionType === EventAdmissionType.ticket,
		[EventGetterKeys.currentEventHasAgeRequirement]: (state): boolean =>
			Boolean(
				state.currentEvent?.applicationInfo?.minAge ||
					state.currentEvent?.applicationInfo?.maxAge,
			),
		[EventGetterKeys.getOptionById]: state => (optionId: string) => {
			return state.currentEvent?.options?.find(o => o.id === optionId);
		},
		[EventGetterKeys.currentApplicationOption]: (state, getters) =>
			getters[EventGetterKeys.getOptionById](
				state.currentApplication?.optionId,
			),
		[EventGetterKeys.currentApplicationFeeType]: (state, getters) => {
			if (!state.currentApplication?.appliedItems.length) {
				return undefined;
			}
			return getters[EventGetterKeys.currentApplicationOption]?.feeTypes.find(
				(fT: EventFeeType) =>
					fT.id === state.currentApplication?.appliedItems[0].feeTypeId,
			);
		},
		[EventGetterKeys.getApplicationFeeTypeById]: (_, getters) => (
			feeTypeId: string,
		) =>
			getters[EventGetterKeys.currentApplicationOption]?.feeTypes.find(
				(fT: EventFeeType) => fT.id === feeTypeId,
			),
		[EventGetterKeys.currentApplicationPaymentType]: state =>
			state.currentEvent?.paymentTypes?.find(
				(pT: EventPaymentType | Partial<EventPaymentType>) =>
					pT.id === state.currentApplication?.paymentTypeId,
			),

		[EventGetterKeys.getScheduleInstancePageDataByOptionId]: state => (
			optionId: string,
		) =>
			state.scheduleInstancePageData[optionId] ?? {
				items: [],
				total: 0,
			},

		[EventGetterKeys.attendancePageData]: state => state.attendancePageData,

		[EventGetterKeys.currentScheduleInstance]: state =>
			state.currentScheduleInstance,
		[EventGetterKeys.attendanceOfflineFullList]: state =>
			state.attendanceOfflineFullList,
		[EventGetterKeys.attendanceFullList]: state => state.attendanceFullList,
		[EventGetterKeys.offlineCheckins]: state => state.offlineCheckins,
		[EventGetterKeys.getTicketPageDataByOptionId]: state => (
			optionId: string,
		) =>
			state.ticketPageData[optionId] ?? {
				items: [],
				total: 0,
			},
		[EventGetterKeys.attendanceImportData]: state =>
			state.attendanceImportData.map(item => ({
				...item,
				// calculate isSelectable
				isSelectable:
					// new contact + new application
					item.isNewContact ||
					// existing contact must be single
					item.matchType === AttendanceImportMatchType.single,
				// more validation for existing application deferred to when the item is selected
			})),
		[EventGetterKeys.firstFeeTypeIdByScheduleId]: state => (
			scheduleId: string,
		): string => {
			const option = state.currentEvent?.options?.find(o =>
				o.schedules?.some(s => s.id === scheduleId),
			);

			if (!option) {
				// impossible, just type guard
				throw `Option not found for schedule Id "${scheduleId}"`;
			}

			return option.feeTypes?.[0].id as string;
		},
		[EventGetterKeys.allFeeTypesByScheduleId]: state => (
			scheduleId: string,
		): EventFeeType[] =>
			state.currentEvent?.options?.find(o =>
				o.schedules?.find(s => s.id === scheduleId),
			)?.feeTypes ?? [],
		[EventGetterKeys.applicationCache]: state => state.applicationCache,
	},
};

export default store;
